/**
 * Daylight Mode — time-of-day bands for Sales Portal ambience (landscape + glass UI).
 *
 * Bands use the browser's local hour (0–23). To disable, turn off the toggle in Sales Settings.
 * Scenes and glass tokens: client/src/styles/sales-portal-ambience.css
 * Service Portal never uses these bands.
 */

export const DAYLIGHT_MODE_BANDS = [
  "dawn",
  "morning",
  "noon",
  "afternoon",
  "dusk",
  "night",
] as const;

export type DaylightModeBand = (typeof DAYLIGHT_MODE_BANDS)[number];

export const DAYLIGHT_MODE_BAND_LABELS: Record<DaylightModeBand, string> = {
  dawn: "Dawn (5am–7am)",
  morning: "Morning (7am–11am)",
  noon: "Noon (11am–3pm)",
  afternoon: "Afternoon (3pm–6pm)",
  dusk: "Dusk (6pm–8pm)",
  night: "Night (8pm–5am)",
};

/**
 * Resolve the active band from a local hour (0–23).
 *
 * | Band      | Hours        |
 * |-----------|--------------|
 * | dawn      | 5 ≤ h < 7    |
 * | morning   | 7 ≤ h < 11   |
 * | noon      | 11 ≤ h < 15  |
 * | afternoon | 15 ≤ h < 18  |
 * | dusk      | 18 ≤ h < 20  |
 * | night     | 20 ≤ h or h < 5 |
 */
export function resolveDaylightModeBandFromHour(hour: number): DaylightModeBand {
  const h = Math.floor(hour);
  if (h >= 5 && h < 7) return "dawn";
  if (h >= 7 && h < 11) return "morning";
  if (h >= 11 && h < 15) return "noon";
  if (h >= 15 && h < 18) return "afternoon";
  if (h >= 18 && h < 20) return "dusk";
  return "night";
}

export function resolveDaylightModeBandFromDate(date: Date = new Date()): DaylightModeBand {
  return resolveDaylightModeBandFromHour(date.getHours());
}

export function isDaylightModeBand(value: string): value is DaylightModeBand {
  return (DAYLIGHT_MODE_BANDS as readonly string[]).includes(value);
}

/** CSS/JS duration for Daylight Mode colour cross-fades (1.5–2.5s range). */
export const DAYLIGHT_MODE_TRANSITION_MS = 2000;

/** Darker ambience scenes — use the light Clarity logo for contrast. */
export const DAYLIGHT_DARK_LOGO_BANDS: ReadonlySet<DaylightModeBand> = new Set(["dawn", "dusk", "night"]);

/** Brighter scenes — use the standard (dark) Clarity logo. */
export function salesAmbienceUsesLightLogo(band: DaylightModeBand): boolean {
  return DAYLIGHT_DARK_LOGO_BANDS.has(band);
}
